import {
	Id,
	LaravelResponse,
	PaginatedListFetcherConfig,
	keyToUrl,
	mapPaginatedFetcherConfigToAxiosConfig,
	withDestroyEndpoint,
	withIndexEndpoint,
	withOptionsEndpoint,
	withStoreEndpoint,
} from "@dgs/core";
import axios from "axios";
import { Interval, format } from "date-fns";
import { EventCalendarResource } from "../../../../../shared/calendar/events/calendarEventService";
import { EventIndexResource, EventListOverviewResource, EventSaveRequest } from "../../../../../shared/types/event";
import {
	AnalyticsEventFilterRequest,
	AnalyticsFeedbackEventOptionResource,
	AnalyticsFeedbackEventResource,
	EventAnalyticsResource,
	EventBudgetAnalyticsResource,
} from "../../../analytics/analyticsTypes";
import {
	ApprovableEventOptionResource,
	ClippingProviderContactResource,
	EventDetailResource,
	EventOptionResource,
	IEventFilter,
} from "../../types";
import { withEventContactEndpoints } from "./withEventContactEndpoints";

export const userEventService = (id: Id) => {
	const userEventServiceBaseKey = ["users", `${id}`, "events"];

	return {
		...withIndexEndpoint<EventIndexResource>(userEventServiceBaseKey),
		detachEvents: (eventId: Id) =>
			axios.post<LaravelResponse<void>>(`${keyToUrl([...userEventServiceBaseKey, `${eventId}`, "detach"])}`),
	};
};

const baseKey = "events";
const optionsForFeedbackAnalyticsKey = ["events", "options-for-feedback-analytics"];
const calendarKey = ["events", "calendar"];
const filteredArchivedIndexKey = ["events", "archived"];
const listOverviewKey = ["events", "overview", "list"];
const eventExportKey = ["events", "export"];
const archivedEventExportKey = ["events", "archived", "export"];
const analyticsKey = ["events", "analytics"];
const budgetAnalyticsKey = ["events", "budget-analytics"];
const approvableOptionsKey = ["events", "approvable-options"];
const optionsWithParticipantManagementKey = [baseKey, "options", "with-participant-management"];
const detailKey = (eventId: Id) => [baseKey, `${eventId}`, "details"];
const clippingProviderContactsKey = (eventId: Id) => [baseKey, `${eventId}`, "clipping-provider-contacts"];

const showEventForFeedbackAnalyticsKey = (eventId: Id) => [baseKey, `${eventId}`, "option-for-feedback-analytics"];

export const eventService = {
	...withIndexEndpoint<EventIndexResource>([baseKey]),
	...withOptionsEndpoint<EventOptionResource>([baseKey]),
	...withStoreEndpoint<EventSaveRequest>([baseKey]),
	...withDestroyEndpoint([baseKey]),
	...withEventContactEndpoints([baseKey]),
	optionsForFeedbackAnalyticsKey,
	optionsForFeedbackAnalytics: (search: string, page: number, params?: Record<string, any>) => {
		return axios.get<LaravelResponse<AnalyticsFeedbackEventOptionResource[]>>(
			keyToUrl(optionsForFeedbackAnalyticsKey),
			{
				params: { ...params, search, page },
			},
		);
	},
	showEventForFeedbackAnalyticsKey,
	showEventForFeedbackAnalytics: (eventId: Id) => {
		return axios.get<LaravelResponse<AnalyticsFeedbackEventResource>>(
			keyToUrl(showEventForFeedbackAnalyticsKey(eventId)),
		);
	},
	filteredIndex(config: PaginatedListFetcherConfig<EventIndexResource>, filterId: Id | null) {
		return axios.get<LaravelResponse<EventIndexResource[]>>(keyToUrl([baseKey]), {
			params: { ...config.searchParams, ...config.pageParams, filterId },
		});
	},
	filteredArchivedIndexKey,
	filteredArchivedIndex(config: PaginatedListFetcherConfig<EventIndexResource>, filterId: Id | null) {
		return axios.get<LaravelResponse<EventIndexResource[]>>(keyToUrl(filteredArchivedIndexKey), {
			params: { ...config.searchParams, ...config.pageParams, filterId },
		});
	},
	detailKey,
	detail: (eventId: Id) => axios.get<LaravelResponse<EventDetailResource>>(keyToUrl(detailKey(eventId))),
	calendarKey,
	calendar: (dateRange: Interval, filter: IEventFilter, search: string | null) =>
		axios.post<LaravelResponse<EventCalendarResource[]>>(keyToUrl(calendarKey), {
			...dateRange,
			start: format(dateRange.start, "yyyy-MM-dd"),
			end: format(dateRange.end, "yyyy-MM-dd"),
			search,
			...filter,
		}),
	listOverviewKey,
	listOverview: (
		config: PaginatedListFetcherConfig<EventListOverviewResource>,
		filterId: Id | null,
		showOnlyOwnEvents: boolean,
	) =>
		axios.get<LaravelResponse<EventListOverviewResource[]>>(keyToUrl(listOverviewKey), {
			params: {
				...config.searchParams,
				...config.pageParams,
				...config.sortParams,
				filterId,
				showOnlyOwnEvents: showOnlyOwnEvents ? "1" : "0",
			},
		}),
	clippingProviderContactsKey,
	clippingProviderContacts: (config: PaginatedListFetcherConfig<ClippingProviderContactResource>, eventId: Id) => {
		return axios.get<LaravelResponse<ClippingProviderContactResource[]>>(
			keyToUrl(clippingProviderContactsKey(eventId)),
			mapPaginatedFetcherConfigToAxiosConfig(config),
		);
	},
	exportEvents: (search: string | null, filterId: Id | null, showOnlyOwnEvents?: boolean) => {
		return axios.get<undefined>(keyToUrl(eventExportKey), {
			params: { search, filterId, showOnlyOwnEvents: showOnlyOwnEvents ? "1" : "0" },
		});
	},
	exportArchivedEvents: (search: string | null, filterId: Id | null) => {
		return axios.get<undefined>(keyToUrl(archivedEventExportKey), { params: { search, filterId } });
	},
	analyticsKey,
	analytics: (filter: AnalyticsEventFilterRequest) =>
		axios.post<LaravelResponse<EventAnalyticsResource>>(keyToUrl(analyticsKey), filter),
	budgetAnalyticsKey,
	budgetAnalytics: (filter: AnalyticsEventFilterRequest) =>
		axios.post<LaravelResponse<EventBudgetAnalyticsResource>>(keyToUrl(budgetAnalyticsKey), filter),
	approvableOptionsKey,
	approvableOptions: (search: string, page: number, params?: Record<string, any>) => {
		return axios.get<LaravelResponse<ApprovableEventOptionResource[]>>(keyToUrl(approvableOptionsKey), {
			params: { ...params, search, page },
		});
	},
	optionsWithParticipantManagementKey,
	optionsWithParticipantManagement: (search: string, page: number, params?: Record<string, any>) => {
		return axios.get<LaravelResponse<EventOptionResource[]>>(keyToUrl(optionsWithParticipantManagementKey), {
			params: { ...params, search, page },
		});
	},
};
